import React, { useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { getSlackDirectory, setUserSlackAccount } from '@/store/actions/slack';
import { selectSlackDirectory } from '@/store/selectors/slack';
import { SlackMember } from '@/store/reducers/slack';
import { selectUser } from '@/store/selectors/users';

import { HandleToggle } from '@/components/common/hooks/useToggle';
import UseWindowScrollBlock from '@/components/common/hooks/useWindowScrollBlock';
import useLanguage from '@/components/common/hooks/useLanguage';
import useInput from '@/components/common/hooks/useInput';
import BottomSheet from '@/components/payout/BottomSheet';
import PlusIcon from '@/components/common/icons/catalogs/PlusIcon';
import CheckBoxOnIcon from '@/components/common/icons/CheckBoxOnIcon';
import CheckBoxOffIcon from '@/components/common/icons/CheckBoxOffIcon';
import search from '@/utils/search';

interface Props {
  hideModal: HandleToggle;
}

/**
 * Admin picks the Slack account behind this employee.
 *
 * Only accounts nobody else claims are offered, plus the one already linked. That is
 * what makes the holiday poll able to tag them and to count their reaction -- an
 * unlinked person is invisible to it.
 *
 * Goes through BottomSheet, held off-screen until the directory has arrived. A hand-rolled
 * sheet showed a Loader first -- a page-level, position: fixed component that painted a
 * narrow band in the middle of the screen -- and then grew into the list.
 */
const ModalSlackAccount: React.FC<Props> = ({ hideModal }) => {
  UseWindowScrollBlock();

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const directory = useSelector(selectSlackDirectory);
  const [searchValue, setSearchValue] = useInput('');
  const [{ common, holidays: text }] = useLanguage();

  useLayoutEffect(() => {
    dispatch(getSlackDirectory());
  }, []);

  // The account on this person. Falls back to what the user record carries when Slack's
  // member list does not include it (a deactivated account), so a link never looks
  // absent just because the directory could not name it.
  const linked = useMemo((): SlackMember | undefined => {
    if (!user?.slackUserId) return undefined;

    return directory?.links.find((link) => link.slackUserId === user.slackUserId)
      || { slackUserId: user.slackUserId, name: user.slackUserName || user.slackUserId };
  }, [directory, user]);

  // Free accounts, with the one already on this person kept at the top so it can be
  // seen and cleared.
  const options = useMemo(() => {
    if (!directory) return [];

    const free = directory.unlinked;
    const all = linked ? [linked, ...free] : free;

    return search(all, searchValue, (member) => `${member.name} ${member.email || ''}`);
  }, [directory, linked, searchValue]);

  const choose = (slackUserId: string) => {
    if (!user) return;

    dispatch(setUserSlackAccount(user._id, slackUserId));
    hideModal(false);
  };

  const clear = () => {
    if (!user) return;

    dispatch(setUserSlackAccount(user._id, null));
    hideModal(false);
  };

  return (
    <BottomSheet isReady={Boolean(directory && user)} onClose={() => hideModal(false)}>
      {directory && (
        <>
          <Title>{text.slack_account}</Title>
          <Hint>{text.slack_account_hint}</Hint>

          <SearchWrap>
            <SearchItem
              type="text"
              value={searchValue}
              onChange={setSearchValue}
              placeholder={common.search}
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              autoCapitalize="off"
            />
            <PlusButton visibility={searchValue} onClick={() => setSearchValue('')}>
              <PlusIcon width="27px" height="27px" />
            </PlusButton>
          </SearchWrap>

          <List>
            {options.length === 0 && (
              <Muted>{text.no_free_slack_accounts}</Muted>
            )}
            {options.map((member) => {
              const isCurrent = member.slackUserId === user?.slackUserId;

              return (
                <Item
                  key={member.slackUserId}
                  isCurrent={isCurrent}
                  onClick={() => choose(member.slackUserId)}
                >
                  <ItemText>
                    <ItemName>{member.name}</ItemName>
                    <ItemSub>
                      {member.email || member.displayName || member.slackUserId}
                      {isCurrent ? text.current_account : ''}
                    </ItemSub>
                  </ItemText>
                  {/* The same checkbox the project Slack-channel picker uses. */}
                  <CheckBoxWrap>
                    {isCurrent ? <CheckBoxOnIcon /> : <CheckBoxOffIcon />}
                  </CheckBoxWrap>
                </Item>
              );
            })}
          </List>

          {Boolean(user?.slackUserId) && (
            <Danger onClick={clear}>{text.unlink_slack}</Danger>
          )}
        </>
      )}
    </BottomSheet>
  );
};

const Title = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #21272e;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #7d8895;
  margin: 4px 0 16px;
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 8px;
`;

const SearchItem = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 40px 0 12px;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  font-size: 14px;
  color: #21272e;
`;

const PlusButton = styled.div<{ visibility: string }>`
  position: absolute;
  right: 8px;
  top: 6px;
  transform: rotate(45deg);
  cursor: pointer;
  visibility: ${({ visibility }) => (visibility ? 'visible' : 'hidden')};
`;

// No scroll box of its own: the sheet scrolls.
const List = styled.div``;

const Item = styled.div<{ isCurrent: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 8px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ isCurrent }) => (isCurrent ? '#f2f7ff' : 'transparent')};

  &:hover {
    background: #f5f6f7;
  }
`;

const ItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const CheckBoxWrap = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const ItemName = styled.div`
  font-size: 15px;
  color: #21272e;
`;

const ItemSub = styled.div`
  font-size: 12px;
  color: #7d8895;
`;

const Muted = styled.div`
  font-size: 14px;
  color: #b4babf;
  padding: 12px 8px;
`;

const Danger = styled.button`
  margin-top: 16px;
  border: none;
  background: none;
  align-self: flex-start;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 700;
  color: #eb5757;
  cursor: pointer;
`;

export default ModalSlackAccount;
